import type { RFQ, RFQQuote, RFQQuoteResponse } from '@/types/api/rfq'

export interface GetRFQEventsParams {
  rfq_id?: string
  // Message-number HWMs are 18-digit values that exceed Number's safe-integer
  // precision, so they are passed as strings.
  start?: string
  end?: string
  event_type?: RFQEventType[]
}

export enum RFQEventType {
  Ack = 'ack',
  Approve = 'approve',
  Reject = 'reject',
  Cancel = 'cancel',
  CancelReject = 'cancel_reject',
  QuoteUpdate = 'quote_update',
  Expire = 'expire',
  Fill = 'fill',
  QuoteResponseReceive = 'quote_response_receive',
  QuoteResponseApprove = 'quote_response_approve',
  QuoteResponseReject = 'quote_response_reject',
  QuoteResponseCancel = 'quote_response_cancel',
  DueTime = 'due_time',
  FirmUntilTime = 'firm_until_time',
}

interface RFQUpdateEventBase {
  time: string
  rfq_id: string
  client_rfq_id: string
}

interface RFQAckEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.Ack
  ib_id: string
}

export interface RFQApproveEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.Approve
  ib_id: string
}

export interface RFQRejectEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.Reject
  ib_id: string
  reason: string
}

export interface RFQCancelEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.Cancel
  ib_id: string
  reason: string
  cancel_request_id: string
}

interface RFQCancelRejectEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.CancelReject
  ib_id: string
  reason: string
  cancel_request_id: string
}

interface RFQQuoteUpdateEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.QuoteUpdate
  venue_update_id?: string
  quotes?: RFQQuote[]
}

export interface RFQExpireEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.Expire
  ib_id: string
  rfq_expired_at: string
}

interface RFQFillEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.Fill
  ib_id: string
  contra_quote_id: string
  order_id: string
  client_order_id: string
  price: number
  filled_amount: number
  accrued_interest_cents: number
}

interface RFQQuoteResponseReceiveEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.QuoteResponseReceive
  order_id: string
  client_order_id: string
  isin: string
  price: number
  amount: number
  side: 'buy' | 'sell'
}

interface RFQQuoteResponseApproveEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.QuoteResponseApprove
  order_id: string
  contra_quote_id: string
}

interface RFQQuoteResponseRejectEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.QuoteResponseReject
  order_id: string
  contra_quote_id: string
  active_quotes: RFQQuote[]
  reason: string
  active_order?: RFQQuoteResponse
}

interface RFQQuoteResponseCancelEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.QuoteResponseCancel
  order_id: string
  contra_quote_id: string
  client_order_id: string
  reason: string
}

interface RFQDueTimeEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.DueTime
  ib_id: string
  due_time: string
}

interface RFQFirmUntilTimeEvent extends RFQUpdateEventBase {
  event_type: RFQEventType.FirmUntilTime
  ib_id: string
  firm_until_time: string
}

export type RFQUpdateEvent =
  | RFQAckEvent
  | RFQApproveEvent
  | RFQRejectEvent
  | RFQCancelEvent
  | RFQCancelRejectEvent
  | RFQQuoteUpdateEvent
  | RFQExpireEvent
  | RFQFillEvent
  | RFQQuoteResponseReceiveEvent
  | RFQQuoteResponseApproveEvent
  | RFQQuoteResponseRejectEvent
  | RFQQuoteResponseCancelEvent
  | RFQDueTimeEvent
  | RFQFirmUntilTimeEvent

//
// RFQ event messages (these wrap the events above into a message)
//

export interface RFQUpdateEventMessage {
  msg_type: 'rfq_update' | 'system_update' | 'connected'
  event_id: string
  msg_number: string
  summary: RFQ
  event: RFQUpdateEvent
}
